import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/roles/$roleKey')({
  component: RoleEditorPage,
})

type Action = 'read' | 'create' | 'update' | 'delete' | 'manage' | 'reassign'

/** Stable display order for action columns. Entities that don't enforce
 *  a particular action simply omit its column — this is the canonical
 *  order the UI lays them out in when they are shown. */
const ACTION_ORDER: readonly Action[] = ['read', 'create', 'update', 'delete', 'manage', 'reassign']

const ACTION_LABELS: Record<Action, string> = {
  read: 'Read',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  manage: 'Manage',
  reassign: 'Reassign',
}

interface Grant {
  moduleKey: string | null
  entityKey: string | null
  fieldKey: string | null
  action: Action
  allowed: boolean
}

interface EntityRegistryView {
  entityKey: string
  fields: string[]
  sensitive: string[]
  actions: Action[]
}

interface ModuleRegistryView {
  moduleKey: string
  entities: EntityRegistryView[]
}

function RoleEditorPage() {
  const { roleKey: roleId } = useParams({ from: '/settings/roles/$roleKey' })
  const { data: roleData, isLoading } = useQuery(
    orpc.rbac.roles.get.queryOptions({ input: { id: roleId } }),
  )
  // Fetch the module/entity/action map from the backend — this is the
  // source of truth for which action columns to render per entity. The
  // editor used to hardcode HR only; now it reflects every registered
  // module (hr, crm, rbac, and whatever else ships a registry).
  const { data: registriesData } = useQuery(orpc.rbac.registries.list.queryOptions())

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [grants, setGrants] = useState<Grant[]>([])
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (roleData && !dirty) {
      const r = roleData as {
        name: string
        description: string | null
        grants: Grant[]
      }
      setName(r.name)
      setDescription(r.description ?? '')
      setGrants(r.grants ?? [])
    }
  }, [roleData, dirty])

  const updateMutation = useMutation(
    orpc.rbac.roles.update.mutationOptions({
      onSuccess: () => {
        toast.success('Role updated')
        setDirty(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const getGrant = (
    moduleKey: string | null,
    entityKey: string | null,
    fieldKey: string | null,
    action: Action,
  ): Grant | undefined =>
    grants.find(
      (g) =>
        g.moduleKey === moduleKey &&
        g.entityKey === entityKey &&
        g.fieldKey === fieldKey &&
        g.action === action,
    )

  const toggleGrant = (
    moduleKey: string,
    entityKey: string | null,
    fieldKey: string | null,
    action: Action,
    event?: { ctrlKey?: boolean; metaKey?: boolean },
  ) => {
    const isDeny = event?.ctrlKey || event?.metaKey
    const existing = getGrant(moduleKey, entityKey, fieldKey, action)
    setDirty(true)
    if (!existing) {
      setGrants((g) => [...g, { moduleKey, entityKey, fieldKey, action, allowed: !isDeny }])
    } else if (existing.allowed === !isDeny) {
      setGrants((g) =>
        g.filter(
          (x) =>
            !(
              x.moduleKey === moduleKey &&
              x.entityKey === entityKey &&
              x.fieldKey === fieldKey &&
              x.action === action
            ),
        ),
      )
    } else {
      setGrants((g) =>
        g.map((x) =>
          x.moduleKey === moduleKey &&
          x.entityKey === entityKey &&
          x.fieldKey === fieldKey &&
          x.action === action
            ? { ...x, allowed: !isDeny }
            : x,
        ),
      )
    }
  }

  if (isLoading) return <div className="container mx-auto py-6 px-4">Loading...</div>
  if (!roleData) return <div className="container mx-auto py-6 px-4">Role not found</div>

  const role = roleData as {
    id: string
    name: string
    description: string | null
    isSystem: boolean
  }

  const modules: ModuleRegistryView[] = (registriesData as ModuleRegistryView[] | undefined) ?? []

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/settings/roles">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{role.name}</h1>
          {role.isSystem && <Badge variant="outline">System role (read-only)</Badge>}
        </div>
        {dirty && !role.isSystem && (
          <Button
            onClick={() =>
              updateMutation.mutate({
                id: role.id,
                name,
                description,
                grants,
              })
            }
            disabled={updateMutation.isPending}
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        )}
      </div>

      <Card className="mb-6">
        <CardContent className="p-4 space-y-3">
          <div>
            <label htmlFor="role-name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="role-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setDirty(true)
              }}
              disabled={role.isSystem}
            />
          </div>
          <div>
            <label htmlFor="role-description" className="text-sm font-medium">
              Description
            </label>
            <Textarea
              id="role-description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value)
                setDirty(true)
              }}
              disabled={role.isSystem}
            />
          </div>
        </CardContent>
      </Card>

      {modules.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            Loading module registry…
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue={modules[0]?.moduleKey}>
          <TabsList>
            {modules.map((m) => (
              <TabsTrigger key={m.moduleKey} value={m.moduleKey}>
                {m.moduleKey.toUpperCase()}
              </TabsTrigger>
            ))}
          </TabsList>
          {modules.map((m) => (
            <TabsContent key={m.moduleKey} value={m.moduleKey}>
              <Card>
                <CardContent className="p-4 space-y-6">
                  {m.entities.map((entity) => (
                    <GrantGrid
                      key={entity.entityKey}
                      moduleKey={m.moduleKey}
                      entity={entity}
                      getGrant={getGrant}
                      toggleGrant={toggleGrant}
                      disabled={role.isSystem}
                    />
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  )
}

interface GrantGridProps {
  moduleKey: string
  entity: EntityRegistryView
  getGrant: (m: string | null, e: string | null, f: string | null, a: Action) => Grant | undefined
  toggleGrant: (
    m: string,
    e: string | null,
    f: string | null,
    a: Action,
    ev?: { ctrlKey?: boolean; metaKey?: boolean },
  ) => void
  disabled: boolean
}

function GrantGrid({ moduleKey, entity, getGrant, toggleGrant, disabled }: GrantGridProps) {
  const [showFields, setShowFields] = useState(false)

  // Filter to the entity's actual action set, preserving the canonical
  // display order. Entities that don't enforce `reassign` or `manage`
  // don't get a column for it — we won't render toggles for inert grants.
  const actions = ACTION_ORDER.filter((a) => entity.actions.includes(a))
  const gridStyle = { gridTemplateColumns: `minmax(10rem, 1fr) repeat(${actions.length}, 5rem)` }

  return (
    <div className="border rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold capitalize">{entity.entityKey.replace(/_/g, ' ')}</h3>
        <Button variant="ghost" size="sm" onClick={() => setShowFields((v) => !v)}>
          {showFields ? 'Hide' : 'Show'} fields ({entity.fields.length})
        </Button>
      </div>

      <div
        className="grid gap-2 items-center pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground border-b mb-1"
        style={gridStyle}
      >
        <div />
        {actions.map((a) => (
          <div key={a} className="text-center">
            {ACTION_LABELS[a]}
          </div>
        ))}
      </div>

      <div className="grid gap-2 items-center py-1 text-sm" style={gridStyle}>
        <div className="font-medium">All fields</div>
        {actions.map((action) => {
          const grant = getGrant(moduleKey, entity.entityKey, null, action)
          return (
            <GrantCheckbox
              key={action}
              state={grant ? (grant.allowed ? 'allow' : 'deny') : 'unset'}
              action={action}
              disabled={disabled}
              onClick={(ev) => toggleGrant(moduleKey, entity.entityKey, null, action, ev)}
            />
          )
        })}
      </div>

      {showFields &&
        entity.fields.map((field) => {
          const sensitive = entity.sensitive.includes(field)
          return (
            <div
              key={field}
              className="grid gap-2 items-center py-1 text-sm pl-4 border-t mt-1"
              style={gridStyle}
            >
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">{field}</span>
                {sensitive && (
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 text-[10px]">
                    sensitive
                  </Badge>
                )}
              </div>
              {actions.map((action) => {
                const grant = getGrant(moduleKey, entity.entityKey, field, action)
                return (
                  <GrantCheckbox
                    key={action}
                    state={grant ? (grant.allowed ? 'allow' : 'deny') : 'unset'}
                    action={action}
                    disabled={disabled}
                    onClick={(ev) => toggleGrant(moduleKey, entity.entityKey, field, action, ev)}
                  />
                )
              })}
            </div>
          )
        })}
    </div>
  )
}

function GrantCheckbox({
  state,
  action,
  disabled,
  onClick,
}: {
  state: 'unset' | 'allow' | 'deny'
  action: Action
  disabled: boolean
  onClick: (ev: { ctrlKey?: boolean; metaKey?: boolean }) => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => onClick({ ctrlKey: e.ctrlKey, metaKey: e.metaKey })}
      className={
        'h-7 rounded border text-xs transition-colors ' +
        (state === 'allow'
          ? 'bg-green-100 border-green-400 text-green-800'
          : state === 'deny'
            ? 'bg-red-100 border-red-400 text-red-800'
            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50')
      }
      title={`${action}${state !== 'unset' ? ` (${state})` : ''} — Ctrl+click to toggle deny`}
    >
      {state === 'allow' ? '✓' : state === 'deny' ? '⊘' : ''}
    </button>
  )
}
